import { saveAs } from 'file-saver'
import { isBlank, sumSkipBlank, SPOT_GRANULARITIES } from './useSpotSettlement.js'

/**
 * 现货收益明细导出（CSV，Excel 可直接打开）。
 *
 * 用 CSV 而不是 xlsx：项目没有引入 xlsx/exceljs，明细本身是纯二维数据，
 * 加个 BOM 就能让 Excel 正确识别 UTF-8，没必要为此背一个几百 KB 的依赖。
 */

/** 列定义：key 取值，label 表头。与 SpotDetailTablePanel 的列保持一致 */
const BASE_COLUMNS = [
  { key: 'settleDate', label: '日期', text: true },
  { key: 'timeSlot', label: '时段', text: true, skipOn: 'day' },
  { key: 'chargeEnergy', label: '充电量(kWh)', sum: true },
  { key: 'dischargeEnergy', label: '放电量(kWh)', sum: true },
  { key: 'realtimeIncome', label: '实时收益(元)', sum: true },
  { key: 'dayaheadIncome', label: '日前收益(元)', sum: true },
  { key: 'realtimeAvgPrice', label: '实时均价(元/MWh)' },
  { key: 'dayaheadAvgPrice', label: '日前均价(元/MWh)' },
  { key: 'dischargeIncomeRef', label: '放电收入参考(元)', sum: true },
  { key: 'chargeCostRef', label: '充电成本参考(元)', sum: true },
]

/** 缺价一律导出 --，绝不退化成 0，口径与表格一致；数值保留 2 位小数，与页面展示一致 */
function cellValue(value) {
  return isBlank(value) ? '--' : Number(value).toFixed(2)
}

/** CSV 转义：含逗号/引号/换行时加引号，内部引号翻倍 */
function escapeCsv(value) {
  const str = value === null || value === undefined ? '' : String(value)
  if (/[",\n\r]/.test(str)) return `"${str.replace(/"/g, '""')}"`
  return str
}

/**
 * 导出现货收益明细
 *
 * @param {object} p
 * @param {Array}  p.rows        明细行（全量，非当前页）
 * @param {string} p.granularity 15min | hour | day
 * @param {object} [p.meterMap]  uuid -> 电表名
 * @param {string} [p.stationName]
 * @param {string} [p.startDate]
 * @param {string} [p.endDate]
 */
export function exportSpotDetail({ rows = [], granularity = '15min', meterMap = {}, stationName = '', startDate = '', endDate = '' } = {}) {
  if (!rows.length) return false

  const showMeter = rows.some((r) => r.uuid)
  const columns = BASE_COLUMNS.filter((c) => c.skipOn !== granularity)
  if (showMeter) {
    // 电表列插在时段之后，与表格顺序一致
    const at = columns.findIndex((c) => c.key === 'timeSlot')
    columns.splice(at + 1, 0, {
      key: 'uuid',
      label: '电表',
      text: true,
      format: (uuid) => (uuid ? meterMap[uuid] || uuid : '整站汇总'),
    })
  }

  const granLabel = SPOT_GRANULARITIES.find((g) => g.value === granularity)?.label || granularity
  const lines = []

  // 表头信息块，导出的文件脱离页面后仍能说明自己是什么
  if (stationName) lines.push(escapeCsv(`电站：${stationName}`))
  if (startDate && endDate) lines.push(escapeCsv(`日期区间：${startDate} 至 ${endDate}`))
  lines.push(escapeCsv(`颗粒度：${granLabel}`))
  lines.push('')

  lines.push(columns.map((c) => escapeCsv(c.label)).join(','))

  rows.forEach((row) => {
    lines.push(columns.map((c) => {
      const raw = row?.[c.key]
      if (c.format) return escapeCsv(c.format(raw))
      if (c.text) return escapeCsv(raw ?? '--')
      return escapeCsv(cellValue(raw))
    }).join(','))
  })

  // 合计行：跳过缺价，均价类不合计，与表格 summaryMethod 同口径
  lines.push(columns.map((c, index) => {
    if (index === 0) return escapeCsv('合计')
    if (!c.sum) return ''
    const total = sumSkipBlank(rows, c.key)
    return escapeCsv(total === null ? '--' : total.toFixed(2))
  }).join(','))

  // BOM：没有它 Excel 会把中文表头读成乱码
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' })
  // 文件名里的日期去掉连字符：20260801_20260831
  const compact = (d) => String(d || '').replace(/-/g, '')
  const range = startDate && endDate ? `_${compact(startDate)}_${compact(endDate)}` : ''
  saveAs(blob, `现货收益明细_${stationName || '电站'}${range}_${granLabel}.csv`)
  return true
}
